/* eslint-disable no-unsafe-optional-chaining */
/* eslint-disable no-else-return */
/* eslint-disable react/react-in-jsx-scope */
import React, { useCallback } from 'react';
import * as Yup from 'yup';
import moment from 'moment';
import { Badge } from 'reactstrap';
import { currencyToLocal, NumberWithCommasFormat } from 'utils/constants';
import UserInfo from 'components/UserInfo';
import DateFormatter from 'components/DateFormatter';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setVendorDetails } from 'actions/vendorDetailsAction';
import IndeterminateCheckbox from 'components/common/IndeterminateCheckbox';
import { types } from '../../constants';
import NonRegisteredBadge from './components/NonRegisteredBadge';
import RegisteredBadge from './components/RegisteredBadge';
import { GradeContext } from './GradeContext';

export const GradeBadge = ({ grade }) => {
  const openGradeDetails = React.useContext(GradeContext);
  return (
    <div
      onClick={() => openGradeDetails && openGradeDetails(grade)}
      className="cursor-pointer d-flex flex-column align-items-start"
      aria-hidden="true"
    >
      <Badge color="primary" className="me-1">
        {grade?.name}
      </Badge>
      <p
        style={{
          color: '#6C757D',
          marginBottom: 0,
          lineHeight: '1.2',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {grade?.productSubCategoryManufacturer}
      </p>
    </div>
  );
};

const getVendorId = (value) => {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const VendorClickableUserInfo = ({ vendorId, name, contactName, mobileNumber }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleOpen = useCallback(() => {
    if (!vendorId) return;
    dispatch(
      setVendorDetails({
        status: true,
        selectedVendor: {
          id: vendorId,
          entityName: name || '',
        },
      }),
    );
    navigate('/vendors');
  }, [dispatch, navigate, vendorId, name]);

  if (!vendorId) {
    return <UserInfo name={name} contactName={contactName} mobileNumber={mobileNumber} />;
  }

  return (
    <div
      className="cursor-pointer market-user-link"
      onClick={handleOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleOpen();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <UserInfo name={name} contactName={contactName} mobileNumber={mobileNumber} />
    </div>
  );
};



const renderCreatedByUser = (row) => {
  // Child user: when createdByUser is different from trader
  const trader = row?.trader;
  const createdByUser = row?.createdByUser;

  if (createdByUser && trader && String(createdByUser.id) !== String(trader.id)) {
    const name = `${createdByUser.firstName || ''} ${createdByUser.lastName || ''}`.trim();
    const callingCode = createdByUser.callingCode?.callingCode || '';
    const phone = createdByUser.phone || '';
    const mobileNumber = phone ? `${callingCode}-${phone}` : '';
    return <UserInfo name={name || '-'} mobileNumber={mobileNumber} />;
  }

  // Fallback for REST data
  if (row?.childUserName) {
    const mobileNumber = row?.childUserPhone ? `+91-${row.childUserPhone}` : '';
    return <UserInfo name={row.childUserName} mobileNumber={mobileNumber} />;
  }
  return '-';
};

const renderRoleColumn = (row) => {
  return (
    <div className="px-1 text-capitalize">
      {row?.trader?.traderType?.toLowerCase() || '-'}
    </div>
  );
};

const renderCreatedByCompany = (row) => {
  const userName = row?.trader?.entityName ?? row?.traderName ?? 'Not Available';
  let mobileNumber = '';
  if (row?.trader?.phone) {
    mobileNumber = `${row?.trader?.callingCode?.callingCode || ''}-${row?.trader?.phone}`;
  } else if (row?.createdByPhone) {
    mobileNumber = `+91-${row.createdByPhone}`;
  }
  const vendorId = getVendorId(row?.trader?.id);

  return (
    <VendorClickableUserInfo
      vendorId={vendorId}
      name={userName}
      mobileNumber={mobileNumber}
    />
  );
};

const renderOfferSeller = (row) => {
  let userName = row?.trader?.entityName ?? row?.traderName ?? 'Not Available';
  let mobileNumber = '';
  if (row?.trader?.phone) {
    mobileNumber = `${row?.trader?.callingCode?.callingCode || ''}-${row?.trader?.phone}`;
  } else if (row?.sellerPhone) {
    mobileNumber = `+91-${row.sellerPhone}`;
  }
  let vendorId = getVendorId(row?.trader?.id);

  if (row?.offerCreatedFor === 'NON_REGISTERED_USER') {
    userName = row?.nonRegisteredUserName;
    mobileNumber = `${row?.nonRegisteredUserCallingCode || ''}-${row?.nonRegisteredUserPhoneNumber || ''}`;
    vendorId = null;
  }

  return (
    <>
      <VendorClickableUserInfo
        vendorId={vendorId}
        name={userName}
        mobileNumber={mobileNumber}
      />
      {row?.offerCreatedFor === 'NON_REGISTERED_USER' && <NonRegisteredBadge />}
      {row?.offerCreatedFor === 'REGISTERED_USER' && <RegisteredBadge />}
    </>
  );
};

const renderDemandBuyer = (row) => {
  let userName = row?.traderName ?? row?.trader?.entityName ?? 'Not Available';
  let mobileNumber = '';
  if (row?.buyerPhone) {
    mobileNumber = `+91-${row.buyerPhone}`;
  } else if (row?.trader?.phone) {
    mobileNumber = `${row?.trader?.callingCode?.callingCode}-${row?.trader?.phone}`;
  }
  let vendorId = getVendorId(row?.trader?.id);

  if (row?.demandCreatedFor === 'NON_REGISTERED_USER') {
    userName = row?.nonRegisteredUserName;
    mobileNumber = `${row?.nonRegisteredUserCallingCode}-${row?.nonRegisteredUserPhoneNumber}`;
    vendorId = null;
  }

  return (
    <>
      <VendorClickableUserInfo
        vendorId={vendorId}
        name={userName}
        mobileNumber={mobileNumber}
      />
      {row?.demandCreatedFor === 'NON_REGISTERED_USER' && (
        <NonRegisteredBadge />
      )}
      {row?.demandCreatedFor === 'REGISTERED_USER' && <RegisteredBadge />}
    </>
  );
};

const renderAssignedManager = (manager) => {
  const name = `${manager?.firstName || ''} ${manager?.lastName || ''}`.trim();
  if (!manager || !name) return '-';

  const phone = manager.phone || '';
  const callingCode = manager.callingCode?.callingCode || '';
  const mobileNumber = phone ? `${callingCode}${callingCode ? '-' : ''}${phone}` : '';

  return (
    <div className="d-flex flex-column" style={{ lineHeight: 1.25, minWidth: 130 }}>
      <span style={{ whiteSpace: 'nowrap' }}>{name}</span>
      {mobileNumber && <span className="text-muted f-12" style={{ whiteSpace: 'nowrap' }}>{mobileNumber}</span>}
    </div>
  );
};

const renderRM = ({ row }) => renderAssignedManager(row?.original?.trader?.dedicatedRM);
const renderPM = ({ row }) => renderAssignedManager(row?.original?.trader?.dedicatedPurchaseManager);

export const MARKET_LIST_STATUS = [
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Inactive', value: 'EXPIRED' },
  // { label: 'Expired', value: 'EXPIRED' },
  //   { label: 'Deleted', value: 'DELETED' },
];
export const MARKET_LIST_STATUS_FILTER = [
  { label: 'Active', id: 'ACTIVE' },
  { label: 'Expired', id: 'EXPIRED' },
];

const listingSelectColumn = {
  id: 'select',
  header: ({ table }) => (
    <IndeterminateCheckbox
      checked={table.getIsAllRowsSelected()}
      indeterminate={table.getIsSomeRowsSelected()}
      onChange={table.getToggleAllRowsSelectedHandler()}
    />
  ),
  cell: ({ row }) => (
    <div className="px-1">
      <IndeterminateCheckbox
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect()}
        indeterminate={row.getIsSomeSelected()}
        onChange={row.getToggleSelectedHandler()}
      />
    </div>
  ),
  enableSorting: false,
};

export const columns = [
  // {
  //   id: 'select',
  //   header: ({ table }) => (
  //     <IndeterminateCheckbox
  //       {...{
  //         checked: table.getIsAllRowsSelected(),
  //         indeterminate: table.getIsSomeRowsSelected(),
  //         onChange: table.getToggleAllRowsSelectedHandler(),
  //       }}
  //     />
  //   ),
  //   cell: ({ row }) => (
  //     <div className="px-1">
  //       <IndeterminateCheckbox
  //         {...{
  //           checked: row.getIsSelected(),
  //           disabled: !row.getCanSelect(),
  //           indeterminate: row.getIsSomeSelected(),
  //           onChange: row.getToggleSelectedHandler(),
  //         }}
  //       />
  //     </div>
  //   ),
  // },
  {
    accessorKey: 'seller',
    header: 'SELLER',
    cell: ({ row }) => (
      <UserInfo
        name={`${row?.original?.seller?.firstName} ${row?.original?.seller?.lastName}`}
        mobileNumber={row?.original?.seller?.phone}
      />
    ),
    sort: true,
  },
  { accessorKey: 'id', header: 'ORDERED ID', sort: true },
  {
    accessorKey: 'grade.name',
    header: 'GRADE',
    sort: true,
  },
  {
    accessorKey: 'seller',
    header: 'LOCATION',
    cell: ({ row }) => (
      <>
        <div className="px-1">
          {row?.original?.seller?.addresses[0]?.city?.cityName}
        </div>
        <div className="px-1">
          {row?.original?.seller?.addresses[0]?.state?.stateName}{' '}
        </div>
      </>
    ),
    sort: true,
  },
  {
    accessorKey: 'bid',
    header: 'ORDERED',
    cell: ({ row }) => (
      <div className="px-1">
        {row?.original?.bid?.quantity + row?.original?.bid?.quantityUnit}
      </div>
    ),
    sort: true,
  },
  {
    accessorKey: 'rate',
    header: 'RATE',
    // cell: ({ row }) => (
    //   <div className="px-1">
    //     {row?.original?.rateUnit?.currencyName
    //       ? row?.original?.rateUnit?.currencyName
    //       : '' + row?.original?.price
    //       ? row?.original?.price
    //       : ''}
    //   </div>
    // ),
    sort: true,
  },
  { accessorKey: 'date', header: 'DATE', sort: true },
  { accessorKey: 'status', header: 'STATUS', sort: true },
  { accessorKey: 'actions', header: '', sort: true },
];

export const offersColumns = [
  { accessorKey: 'SNo', header: 'S.NO.', enableSorting: false },

  {
    accessorKey: 'seller',
    header: 'Seller',
    enableSorting: false,
    accessorFn: (row) => renderOfferSeller(row),
    cell: (info) => info.getValue(),
  },

  {
    accessorKey: 'createdByCompany',
    header: 'Created By',
    enableSorting: false,
    accessorFn: (row) => renderCreatedByCompany(row),
    cell: (info) => info.getValue(),
  },

  {
    accessorKey: 'createdBy',
    header: 'Child User',
    enableSorting: false,
    accessorFn: (row) => renderCreatedByUser(row),
    cell: (info) => info.getValue(),
  },

  { accessorKey: 'dedicatedRM', header: 'RM', enableSorting: false, size: 160, minSize: 160, cell: renderRM },
  { accessorKey: 'dedicatedPurchaseManager', header: 'PM', enableSorting: false, size: 160, minSize: 160, cell: renderPM },

  { accessorKey: 'id', header: 'OFFER ID', enableSorting: true },

  {
    accessorKey: 'grade',
    header: 'GRADE',
    cell: ({ row }) =>
      row?.original?.grades?.map((grade) => {
        return <GradeBadge key={grade?.id} grade={grade} />;
      }),
    size: 200,
    minSize: 200,
    enableSorting: true,
  },
  {
    accessorFn: (row) => {
      return (
        <>
          {row?.offerType === 'FOREIGN' ? (
            <div className="mb-1">
              {row?.portOfDischarge?.name || 'Not Available'}
            </div>
          ) : (
            <>
              <div className="mb-1">
                {row.city?.cityName || 'Not Available'}
              </div>
              <div className="font-manrope text-active text-nowrap">
                {row.state?.stateName || 'Not Available'}
              </div>
            </>
          )}
          {/* <div className="mb-1">
            {(row?.offerType === 'FOREIGN' && row?.portOfDischarge?.name) || ''}
            {row.city?.cityName || ''}
          </div>
          <div className="font-manrope text-active">
            {row.state?.stateName || ''}
          </div> */}
        </>
      );
    },
    header: 'LOCATION',
    id: 'city.cityName',
    enableSorting: true,
    cell: (info) => info.getValue(),
  },
  {
    accessorKey: 'totalQty',
    header: 'TOTAL QTY',
    cell: ({ row }) => (
      <>
        {NumberWithCommasFormat(row?.original?.totalQty)}{' '}
        {row?.original?.actualQuantityUnit || row?.original?.quantityUnit}
      </>
    ),
    enableSorting: true,
  },
  {
    accessorKey: 'minimumQty',
    header: 'MIN. QTY',
    cell: ({ row }) => (
      <>
        {NumberWithCommasFormat(row?.original?.minimumQty)}{' '}
        {row?.original?.actualQuantityUnit || row?.original?.quantityUnit}
      </>
    ),
    enableSorting: true,
  },
  {
    header: 'RATE',
    // accessorFn: (row) => {
    //   const price = row?.rate;
    //   return <div className="px-1">{`${price}/kg`}</div>;
    // },
    // cell: (info) => info.getValue(),
    id: 'rate',
    accessorKey: 'rate',
    cell: ({ row }) => (
      <div className="text-nowrap">
        {`${currencyToLocal(
          row?.original?.rate,
          row?.original?.rateUnit?.isoCurrencyCode,
        )}/${row?.original?.quantityUnit}`}
      </div>
    ),
    enableSorting: true,
  },
  { accessorKey: 'status', header: 'Status', enableSorting: true },
  {
    accessorKey: 'validityStartTime',
    header: 'CREATED DATE',
    cell: ({ row }) => (
      <DateFormatter dateTime={row?.original?.validityStartTime} />
    ),
    enableSorting: true,
  },
  {
    accessorKey: 'validityExpireTime',
    header: 'EXPIRY DATE',
    cell: ({ row }) => (
      <DateFormatter dateTime={row?.original?.validityExpireTime} />
    ),
    enableSorting: true,
  },
  // { accessorKey: 'actions', header: '', enableSorting: false },
];

export const foreignOffersColumns = [
  { accessorKey: 'SNo', header: 'S.NO.', enableSorting: false },

  {
    accessorKey: 'seller',
    header: 'Seller',
    enableSorting: false,
    accessorFn: (row) => renderOfferSeller(row),
    cell: (info) => info.getValue(),
  },

  {
    accessorKey: 'createdByCompany',
    header: 'Created By',
    enableSorting: false,
    accessorFn: (row) => renderCreatedByCompany(row),
    cell: (info) => info.getValue(),
  },

  {
    accessorKey: 'createdBy',
    header: 'Child User',
    enableSorting: false,
    accessorFn: (row) => renderCreatedByUser(row),
    cell: (info) => info.getValue(),
  },

  { accessorKey: 'id', header: 'OFFER ID', enableSorting: true },
  {
    accessorKey: 'grade',
    header: 'GRADE',
    cell: ({ row }) =>
      row?.original?.grades?.map((grade) => {
        return <GradeBadge key={grade?.id} grade={grade} />;
      }),
    enableSorting: true,
    size: 200,
    minSize: 200,
  },
  {
    accessorFn: (row) => {
      return (
        <>
          {row?.offerType === 'FOREIGN' ? (
            <div className="mb-1">
              {row?.portsOfDischarge && row.portsOfDischarge.length > 0 ? (
                <div className="me-1">
                  {row.portsOfDischarge
                    .map(
                      (portOfDischarge) =>
                        portOfDischarge?.name || 'Not Available',
                    )
                    .join(', ')}
                </div>
              ) : (
                <div className="me-1">Not Available</div>
              )}
            </div>
          ) : (
            <>
              <div className="mb-1">
                {row.city?.cityName || 'Not Available'}
              </div>
              <div className="font-manrope text-active text-nowrap">
                {row.state?.stateName || 'Not Available'}
              </div>
            </>
          )}
          {/* <div className="mb-1">
            {(row?.offerType === 'FOREIGN' && row?.portOfDischarge?.name) || ''}
            {row.city?.cityName || ''}
          </div>
          <div className="font-manrope text-active">
            {row.state?.stateName || ''}
          </div> */}
        </>
      );
    },
    header: 'LOCATION',
    id: 'city.cityName',
    enableSorting: true,
    cell: (info) => info.getValue(),
  },
  {
    accessorKey: 'totalQty',
    header: 'TOTAL QTY',
    cell: ({ row }) => (
      <>
        {NumberWithCommasFormat(row?.original?.totalQty)}{' '}
        {row?.original?.actualQuantityUnit || row?.original?.quantityUnit}
      </>
    ),
    enableSorting: true,
  },
  {
    accessorKey: 'minimumQty',
    header: 'MIN. QTY',
    cell: ({ row }) => (
      <>
        {NumberWithCommasFormat(row?.original?.minimumQty)}{' '}
        {row?.original?.actualQuantityUnit || row?.original?.quantityUnit}
      </>
    ),
    enableSorting: true,
  },
  {
    header: 'RATE',
    // accessorFn: (row) => {
    //   const price = row?.rate;
    //   return <div className="px-1">{`${price}/kg`}</div>;
    // },
    // cell: (info) => info.getValue(),
    id: 'rate',
    accessorKey: 'rate',
    cell: ({ row }) => (
      <div className="text-nowrap">
        {`${currencyToLocal(
          row?.original?.rate,
          row?.original?.rateUnit?.isoCurrencyCode,
        )}/${row?.original?.quantityUnit}`}
      </div>
    ),
    enableSorting: true,
  },
  { accessorKey: 'status', header: 'Status', enableSorting: true },
  {
    accessorKey: 'expectedShipmentDate',
    header: 'SHIPMENT DATE',
    cell: ({ row }) => (
      <div className="info ms-2">
        <h4 className="f-12 fw-medium mb-1 text-dark">
          {moment(row?.original?.minShipmentDelay).format('D MMM, YYYY')}
        </h4>
        -
        <h4 className="f-12 fw-medium mb-1 text-dark">
          {moment(row?.original?.maxShipmentDelay).format('D MMM, YYYY')}
        </h4>
      </div>
    ),
    enableSorting: true,
  },
  {
    accessorKey: 'role',
    header: 'ROLE',
    enableSorting: false,
    accessorFn: (row) => renderRoleColumn(row),
    cell: (info) => info.getValue(),
  },
  // { accessorKey: 'actions', header: '', enableSorting: false },
];

export const forwardOffersColumns = [
  { accessorKey: 'SNo', header: 'S.NO.', enableSorting: false },

  {
    accessorKey: 'seller',
    header: 'Seller',
    enableSorting: false,
    accessorFn: (row) => renderOfferSeller(row),
    cell: (info) => info.getValue(),
  },

  {
    accessorKey: 'createdByCompany',
    header: 'Created By',
    enableSorting: false,
    accessorFn: (row) => renderCreatedByCompany(row),
    cell: (info) => info.getValue(),
  },

  {
    accessorKey: 'createdBy',
    header: 'Child User',
    enableSorting: false,
    accessorFn: (row) => renderCreatedByUser(row),
    cell: (info) => info.getValue(),
  },

  { accessorKey: 'id', header: 'OFFER ID', enableSorting: true },

  {
    accessorKey: 'grade',
    header: 'GRADE',
    cell: ({ row }) =>
      row?.original?.grades?.map((grade) => {
        return <GradeBadge key={grade?.id} grade={grade} />;
      }),
    enableSorting: true,
    size: 200,
    minSize: 200,
  },
  {
    accessorFn: (row) => {
      return (
        <>
          {row?.offerType === 'FOREIGN' ? (
            <div className="mb-1">
              {row?.portOfDischarge?.name || 'Not Available'}
            </div>
          ) : (
            <>
              <div className="mb-1">
                {row.city?.cityName || 'Not Available'}
              </div>
              <div className="font-manrope text-active text-nowrap">
                {row.state?.stateName || 'Not Available'}
              </div>
            </>
          )}
          {/* <div className="mb-1">
            {(row?.offerType === 'FOREIGN' && row?.portOfDischarge?.name) || ''}
            {row.city?.cityName || ''}
          </div>
          <div className="font-manrope text-active">
            {row.state?.stateName || ''}
          </div> */}
        </>
      );
    },
    header: 'LOCATION',
    id: 'city.cityName',
    enableSorting: true,
    cell: (info) => info.getValue(),
  },
  {
    accessorKey: 'totalQty',
    header: 'TOTAL QTY',
    cell: ({ row }) => (
      <>
        {NumberWithCommasFormat(row?.original?.totalQty)}{' '}
        {row?.original?.actualQuantityUnit || row?.original?.quantityUnit}
      </>
    ),
    enableSorting: true,
  },
  {
    accessorKey: 'minimumQty',
    header: 'MIN. QTY',
    cell: ({ row }) => (
      <>
        {NumberWithCommasFormat(row?.original?.minimumQty)}{' '}
        {row?.original?.actualQuantityUnit || row?.original?.quantityUnit}
      </>
    ),
    enableSorting: true,
  },
  {
    header: 'RATE',
    // accessorFn: (row) => {
    //   const price = row?.rate;
    //   return <div className="px-1">{`${price}/kg`}</div>;
    // },
    // cell: (info) => info.getValue(),
    id: 'rate',
    accessorKey: 'rate',
    cell: ({ row }) => (
      <div className="text-nowrap">
        {`${currencyToLocal(
          row?.original?.rate,
          row?.original?.rateUnit?.isoCurrencyCode,
        )}/${row?.original?.quantityUnit}`}
      </div>
    ),
    enableSorting: true,
  },
  { accessorKey: 'status', header: 'Status', enableSorting: true },
  {
    accessorKey: 'minDeliveryDelay',
    header: 'EXPECTED DELIVERY DATE',
    cell: ({ row }) => (
      <>
        {' '}
        <div className="info ms-2">
          <h4 className="f-12 fw-medium mb-1 text-dark">
            {moment(row?.original?.minDeliveryDelay).format('D MMM, YYYY')} -{' '}
            {moment(row?.original?.maxDeliveryDelay).format('D MMM, YYYY')}
          </h4>
        </div>
      </>
    ),
    enableSorting: true,
  },
  {
    accessorKey: 'role',
    header: 'ROLE',
    enableSorting: false,
    accessorFn: (row) => renderRoleColumn(row),
    cell: (info) => info.getValue(),
  },
  // { accessorKey: 'actions', header: '', enableSorting: false },
];

export const offersInActiveColumns = [
  listingSelectColumn,
  {
    accessorKey: 'reactivate',
    header: 'Reactivate',
    enableSorting: false,
  },
  { accessorKey: 'SNo', header: 'S.NO.', enableSorting: false },

  {
    accessorKey: 'seller',
    header: 'Seller',
    enableSorting: false,
    accessorFn: (row) => renderOfferSeller(row),
    cell: (info) => info.getValue(),
  },

  {
    accessorKey: 'createdByCompany',
    header: 'Created By',
    enableSorting: false,
    accessorFn: (row) => renderCreatedByCompany(row),
    cell: (info) => info.getValue(),
  },

  {
    accessorKey: 'createdBy',
    header: 'Child User',
    enableSorting: false,
    accessorFn: (row) => renderCreatedByUser(row),
    cell: (info) => info.getValue(),
  },

  { accessorKey: 'id', header: 'OFFER ID', enableSorting: true },
  {
    accessorKey: 'offerType',
    header: 'Type',
    enableSorting: true,
  },
  {
    accessorKey: 'grade',
    header: 'GRADE',
    cell: ({ row }) =>
      row?.original?.grades?.map((grade) => {
        return <GradeBadge key={grade?.id} grade={grade} />;
      }),
    size: 200,
    enableSorting: true,
    minSize: 200,
  },
  {
    accessorFn: (row) => {
      return (
        <>
          {row?.offerType === 'FOREIGN' ? (
            <div className="mb-1">
              {row?.portsOfDischarge && row.portsOfDischarge.length > 0 ? (
                <div className="me-1">
                  {row.portsOfDischarge
                    .map(
                      (portOfDischarge) =>
                        portOfDischarge?.name || 'Not Available',
                    )
                    .join(', ')}
                </div>
              ) : (
                <div className="me-1">Not Available</div>
              )}
            </div>
          ) : (
            <>
              <div className="mb-1">
                {row.city?.cityName || 'Not Available'}
              </div>
              <div className="font-manrope text-active text-nowrap">
                {row.state?.stateName || 'Not Available'}
              </div>
            </>
          )}
          {/* <div className="mb-1">
            {(row?.offerType === 'FOREIGN' && row?.portOfDischarge?.name) || ''}
            {row.city?.cityName || ''}
          </div>
          <div className="font-manrope text-active">
            {row.state?.stateName || ''}
          </div> */}
        </>
      );
    },
    header: 'LOCATION',
    id: 'city.cityName',
    enableSorting: true,
    cell: (info) => info.getValue(),
  },
  {
    accessorKey: 'totalQty',
    header: 'TOTAL QTY',
    cell: ({ row }) => (
      <>
        {NumberWithCommasFormat(row?.original?.totalQty)}{' '}
        {row?.original?.actualQuantityUnit || row?.original?.quantityUnit}
      </>
    ),
    enableSorting: true,
  },
  {
    accessorKey: 'minimumQty',
    header: 'MIN. QTY',
    cell: ({ row }) => (
      <>
        {NumberWithCommasFormat(row?.original?.minimumQty)}{' '}
        {row?.original?.actualQuantityUnit || row?.original?.quantityUnit}
      </>
    ),
    enableSorting: true,
  },
  {
    header: 'RATE',
    // accessorFn: (row) => {
    //   const price = row?.rate;
    //   return <div className="px-1">{`${price}/kg`}</div>;
    // },
    // cell: (info) => info.getValue(),
    id: 'rate',
    accessorKey: 'rate',
    cell: ({ row }) => (
      <div className="text-nowrap">
        {`${currencyToLocal(
          row?.original?.rate,
          row?.original?.rateUnit?.isoCurrencyCode,
        )}/${row?.original?.quantityUnit}`}
      </div>
    ),
    enableSorting: true,
  },
  { accessorKey: 'status', header: 'Status', enableSorting: true },
  {
    accessorKey: 'validityStartTime',
    header: 'CREATED DATE',
    cell: ({ row }) => (
      <DateFormatter dateTime={row?.original?.validityStartTime} />
    ),
    enableSorting: true,
  },
  {
    accessorKey: 'validityExpireTime',
    header: 'EXPIRY DATE',
    cell: ({ row }) => (
      <DateFormatter dateTime={row?.original?.validityExpireTime} />
    ),
    enableSorting: true,
  },
];

export const demandsColumns = [
  { accessorKey: 'SNo', header: 'S.NO.', enableSorting: false },

  {
    accessorKey: 'buyer',
    header: 'Buyer',
    enableSorting: false,
    accessorFn: (row) => renderDemandBuyer(row),
    cell: (info) => info.getValue(),
  },

  {
    accessorKey: 'createdByCompany',
    header: 'Created By',
    enableSorting: false,
    accessorFn: (row) => renderCreatedByCompany(row),
    cell: (info) => info.getValue(),
  },

  {
    accessorKey: 'createdBy',
    header: 'Child User',
    enableSorting: false,
    accessorFn: (row) => renderCreatedByUser(row),
    cell: (info) => info.getValue(),
  },

  { accessorKey: 'dedicatedRM', header: 'RM', enableSorting: false, size: 160, minSize: 160, cell: renderRM },
  { accessorKey: 'dedicatedPurchaseManager', header: 'PM', enableSorting: false, size: 160, minSize: 160, cell: renderPM },

  { accessorKey: 'id', header: 'DEMAND ID', enableSorting: true },
  {
    accessorKey: 'grades',
    header: 'GRADE',
    accessorFn: (row) => {
      return row?.grades?.map((grade) => {
        return <GradeBadge key={grade?.id} grade={grade} />;
      });
    },
    cell: (info) => info.getValue(),
    enableSorting: false,
    size: 200,
    minSize: 200,
  },
  {
    accessorFn: (row) => {
      return (
        <>
          {row?.demandType === 'FOREIGN' ? (
            <div className="mb-1">
              {row?.portOfDischarge?.name || 'Not Available'}
            </div>
          ) : (
            <>
              <div className="mb-1">
                {row.city?.cityName || 'Not Available'}
              </div>
              <div className="font-manrope text-active text-nowrap">
                {row.state?.stateName || 'Not Available'}
              </div>
            </>
          )}
          {/* <div className="mb-1">{row.city?.cityName}</div>
          <div className="font-manrope text-active">{row.state?.stateName}</div> */}
        </>
      );
    },
    header: 'LOCATION',
    id: 'city.cityName',
    enableSorting: true,
    cell: (info) => info.getValue(),
  },

  {
    accessorKey: 'totalQty',
    header: 'TOTAL QTY',
    cell: ({ row }) => (
      <>
        {NumberWithCommasFormat(row?.original?.totalQty)}{' '}
        {row?.original?.actualQuantityUnit || row?.original?.quantityUnit}
      </>
    ),
    enableSorting: true,
  },

  {
    header: 'RATE',
    accessorKey: 'rate',
    cell: ({ row }) => (
      <div className="text-nowrap">
        {`${currencyToLocal(
          row?.original?.rate,
          row?.original?.rateUnit?.isoCurrencyCode,
        )}/${row?.original?.quantityUnit}`}
      </div>
    ),
    id: 'rate',
    enableSorting: true,
  },
  // { accessorKey: 'demandType', header: 'TYPE', enableSorting: true },
  { accessorKey: 'status', header: 'Status', enableSorting: true },
  {
    accessorKey: 'validityStartTime',
    header: 'CREATED DATE',
    cell: ({ row }) => (
      <DateFormatter dateTime={row?.original?.validityStartTime} />
    ),
    enableSorting: true,
  },
  {
    accessorKey: 'validityExpireTime',
    header: 'EXPIRY DATE',
    cell: ({ row }) => (
      <DateFormatter dateTime={row?.original?.validityExpireTime} />
    ),
    enableSorting: true,
  },
  // { accessorKey: 'actions', header: '', enableSorting: false },
];

export const forwardDemandsColumns = [
  { accessorKey: 'SNo', header: 'S.NO.', enableSorting: false },

  {
    accessorKey: 'buyer',
    header: 'Buyer',
    enableSorting: false,
    accessorFn: (row) => renderDemandBuyer(row),
    cell: (info) => info.getValue(),
  },

  {
    accessorKey: 'createdByCompany',
    header: 'Created By',
    enableSorting: false,
    accessorFn: (row) => renderCreatedByCompany(row),
    cell: (info) => info.getValue(),
  },

  {
    accessorKey: 'createdBy',
    header: 'Child User',
    enableSorting: false,
    accessorFn: (row) => renderCreatedByUser(row),
    cell: (info) => info.getValue(),
  },

  { accessorKey: 'id', header: 'DEMAND ID', enableSorting: true },
  {
    accessorKey: 'grades',
    header: 'GRADE',
    accessorFn: (row) => {
      return row?.grades?.map((grade) => {
        return <GradeBadge key={grade?.id} grade={grade} />;
      });
    },
    cell: (info) => info.getValue(),
    enableSorting: false,
    size: 200,
    minSize: 200,
  },
  {
    accessorFn: (row) => {
      return (
        <>
          {row?.demandType === 'FOREIGN' ? (
            <div className="mb-1">
              {row?.portsOfDischarge && row.portsOfDischarge.length > 0 ? (
                <div className="me-1">
                  {row.portsOfDischarge
                    .map(
                      (portOfDischarge) =>
                        portOfDischarge?.name || 'Not Available',
                    )
                    .join(', ')}
                </div>
              ) : (
                <div className="me-1">Not Available</div>
              )}
            </div>
          ) : (
            <>
              <div className="mb-1">
                {row.city?.cityName || 'Not Available'}
              </div>
              <div className="font-manrope text-active text-nowrap">
                {row.state?.stateName || 'Not Available'}
              </div>
            </>
          )}
          {/* <div className="mb-1">{row.city?.cityName}</div>
          <div className="font-manrope text-active">{row.state?.stateName}</div> */}
        </>
      );
    },
    header: 'LOCATION',
    id: 'city.cityName',
    enableSorting: true,
    cell: (info) => info.getValue(),
  },

  {
    accessorKey: 'totalQty',
    header: 'TOTAL QTY',
    cell: ({ row }) => (
      <>
        {NumberWithCommasFormat(row?.original?.totalQty)}{' '}
        {row?.original?.actualQuantityUnit || row?.original?.quantityUnit}
      </>
    ),
    enableSorting: true,
  },

  {
    header: 'RATE',
    accessorKey: 'rate',
    cell: ({ row }) => (
      <div className="text-nowrap">
        {`${currencyToLocal(
          row?.original?.rate,
          row?.original?.rateUnit?.isoCurrencyCode,
        )}/${row?.original?.quantityUnit}`}
      </div>
    ),
    id: 'rate',
    enableSorting: true,
  },
  // { accessorKey: 'demandType', header: 'TYPE', enableSorting: true },
  { accessorKey: 'status', header: 'Status', enableSorting: true },
  {
    accessorKey: 'minDeliveryDelay',
    header: 'EXPECTED DELIVERY DATE',
    cell: ({ row }) => (
      <>
        {' '}
        <div className="info ms-2">
          <h4 className="f-12 fw-medium mb-1 text-dark">
            {moment(row?.original?.minDeliveryDelay).format('D MMM, YYYY')} -{' '}
            {moment(row?.original?.maxDeliveryDelay).format('D MMM, YYYY')}
          </h4>
        </div>
      </>
    ),
    enableSorting: true,
  },
  {
    accessorKey: 'role',
    header: 'ROLE',
    enableSorting: false,
    accessorFn: (row) => renderRoleColumn(row),
    cell: (info) => info.getValue(),
  },
  // { accessorKey: 'actions', header: '', enableSorting: false },
];

export const demandsInActiveColumns = [
  listingSelectColumn,
  {
    accessorKey: 'reactivate',
    header: 'Reactivate',
    enableSorting: false,
  },
  { accessorKey: 'SNo', header: 'S.NO.', enableSorting: false },

  {
    accessorKey: 'buyer',
    header: 'Buyer',
    enableSorting: false,
    accessorFn: (row) => renderDemandBuyer(row),
    cell: (info) => info.getValue(),
  },

  {
    accessorKey: 'createdByCompany',
    header: 'Created By',
    enableSorting: false,
    accessorFn: (row) => renderCreatedByCompany(row),
    cell: (info) => info.getValue(),
  },

  {
    accessorKey: 'createdBy',
    header: 'Child User',
    enableSorting: false,
    accessorFn: (row) => renderCreatedByUser(row),
    cell: (info) => info.getValue(),
  },

  { accessorKey: 'id', header: 'DEMAND ID', enableSorting: true },
  {
    accessorKey: 'demandType',
    header: 'Type',
    enableSorting: true,
  },
  {
    accessorKey: 'grades',
    header: 'GRADE',
    accessorFn: (row) => {
      return row?.grades?.map((grade) => {
        return <GradeBadge key={grade?.id} grade={grade} />;
      });
    },
    cell: (info) => info.getValue(),
    enableSorting: false,
    size: 200,
    minSize: 200,
  },
  {
    accessorFn: (row) => {
      return (
        <>
          {row?.demandType === 'FOREIGN' ? (
            <div className="mb-1">
              {row?.portsOfDischarge && row.portsOfDischarge.length > 0 ? (
                <div className="me-1">
                  {row.portsOfDischarge
                    .map(
                      (portOfDischarge) =>
                        portOfDischarge?.name || 'Not Available',
                    )
                    .join(', ')}
                </div>
              ) : (
                <div className="me-1">Not Available</div>
              )}
            </div>
          ) : (
            <>
              <div className="mb-1">
                {row.city?.cityName || 'Not Available'}
              </div>
              <div className="font-manrope text-active text-nowrap">
                {row.state?.stateName || 'Not Available'}
              </div>
            </>
          )}
          {/* <div className="mb-1">{row.city?.cityName}</div>
          <div className="font-manrope text-active">{row.state?.stateName}</div> */}
        </>
      );
    },
    header: 'LOCATION',
    id: 'city.cityName',
    enableSorting: true,
    cell: (info) => info.getValue(),
  },

  {
    accessorKey: 'totalQty',
    header: 'TOTAL QTY',
    cell: ({ row }) => (
      <>
        {NumberWithCommasFormat(row?.original?.totalQty)}{' '}
        {row?.original?.actualQuantityUnit || row?.original?.quantityUnit}
      </>
    ),
    enableSorting: true,
  },

  {
    header: 'RATE',
    accessorKey: 'rate',
    cell: ({ row }) => (
      <div className="text-nowrap">
        {`${currencyToLocal(
          row?.original?.rate,
          row?.original?.rateUnit?.isoCurrencyCode,
        )}/${row?.original?.quantityUnit}`}
      </div>
    ),
    id: 'rate',
    enableSorting: true,
  },
  // { accessorKey: 'demandType', header: 'TYPE', enableSorting: true },
  { accessorKey: 'status', header: 'Status', enableSorting: true },
  {
    accessorKey: 'validityStartTime',
    header: 'CREATED DATE',
    cell: ({ row }) => (
      <DateFormatter dateTime={row?.original?.validityStartTime} />
    ),
    enableSorting: true,
  },
  {
    accessorKey: 'validityExpireTime',
    header: 'EXPIRY DATE',
    cell: ({ row }) => (
      <DateFormatter dateTime={row?.original?.validityExpireTime} />
    ),
    enableSorting: true,
  },
];

const getInitialValues = (type) => {
  const liveInitialValues = {
    type_of_listing: '',
    grade: '',
    rate: '',
    status: '',
    validityInHours: '',
    packagingTypeId: '',
    totalQty: '',
    minimumQty: '',
    state: '',
    city: '',
  };
  const forwardInitialValues = {
    type_of_listing: '',
    grade: '',
    rate: '',
    status: '',
    offer_validity_hours: '',
    package_type: '',
    quantity: '',
    min_quantity: '',
    state: '',
    city: '',
    delivery_time: '',
  };
  const foreignInitialValues = {
    type_of_listing: '',
    grade: '',
    rate: '',
    status: '',
    offer_validity_hours: '',
    package_type: '',
    quantity: '',
    min_quantity: '',
    state: '',
    city: '',
    delivery_date: '',
  };

  if (type === types.LIVE) return liveInitialValues;
  else if (type === types.FORWARD) return forwardInitialValues;
  else return foreignInitialValues;
};
export const initialValues = Object.freeze({
  LIVE: getInitialValues(types.LIVE),
  FORWARD: getInitialValues(types.FORWARD),
  FOREIGN: getInitialValues(types.FOREIGN),
});

export const getLiveInitialValue = (data, type) => {
  const {
    id,
    rate,
    validityInHours,
    status,
    totalQty,
    minimumQty,
    city,
    grade,
    state,
    validityStartTime,
    validityExpireTime,
    trader,
    rateUnit,
    packagingTypeId,
    quantityUnit,

    // countryCurrency,
    // minimumLiftingQty,
  } = data;
  return {
    id,
    type_of_listing: { id: data[type], label: data[type] },
    grade: { id: grade?.id, label: grade?.name },
    rate,
    // countryCurrencyId: countryCurrency?.id,
    status: { id: status, label: status },
    validityInHours,
    packagingTypeId: { id: packagingTypeId?.id, type: packagingTypeId?.type },
    totalQty,
    minimumQty,
    quantityUnit: { id: quantityUnit, label: quantityUnit },
    // minimumLiftingQty,
    state: { id: state?.id, label: state?.stateName },
    city: { id: city?.id, label: city?.cityName },
    trader: trader?.id,
    validityStartTime,
    validityExpireTime,
    rateUnit: { id: rateUnit?.id, label: rateUnit?.isoCurrencyCode },
  };
};

export const getLiveDemandInitialValue = (data, type) => {
  const {
    id,
    rate,
    validityInHours,
    status,
    totalQty,
    minimumQty,
    city,
    grades,
    state,
    packagingType,
    validityStartTime,
    validityExpireTime,
    quantityUnit,
    countryCurrency,
    trader,
    minimumLiftingQty,
    rateUnit,
  } = data;
  return {
    id,
    type_of_listing: { id: data[type], label: data[type] },
    // grade: { id: grades[0]?.id, label: grades[0]?.name },
    grade: grades,
    rate,
    status: { id: status, label: status },
    validityInHours,
    packagingTypeId: { id: packagingType?.id, label: packagingType?.type },
    totalQty,
    minimumQty,
    minimumLiftingQty,
    state: { id: state?.id, label: state?.stateName },
    city: { id: city?.id, label: city?.cityName },
    trader: trader?.id,
    countryCurrencyId: countryCurrency?.id,
    validityStartTime,
    validityExpireTime,
    quantityUnit: { id: quantityUnit, label: quantityUnit },
    rateUnit: { id: rateUnit?.id, label: rateUnit?.isoCurrencyCode },
  };
};

export const getLiveValuesForOffer = (data) => {
  const {
    id,
    rate,
    validityInHours,
    status,
    totalQty,
    minimumQty,
    city,
    grade,
    state,
    packagingTypeId,
    validityStartTime,
    validityExpireTime,
    trader,
    rateUnit,
    quantityUnit,
  } = data;
  return {
    id,
    // demandId: 1,
    gradeId: grade?.id,
    // type_of_listing: data[type]?.label,
    rate,
    rateUnit: rateUnit.id,
    quantityUnit: quantityUnit.id,
    packagingTypeId: packagingTypeId.id,
    validityInHours,
    validityStartTime: Date.parse(validityStartTime),
    validityExpireTime: Date.parse(validityExpireTime),
    totalQty,
    minimumQty,
    city: city?.id,
    state: state?.id,
    status: status?.label,
    trader,
  };
};

export const getLiveValuesForDemand = (data) => {
  const {
    id,
    rate,
    validityInHours,
    status,
    totalQty,
    minimumQty,
    city,
    grade,
    state,
    packagingTypeId,
    quantityUnit,
    validityStartTime,
    validityExpireTime,
    trader,
    countryCurrencyId,
    minimumLiftingQty,
    rateUnit,
  } = data;
  return {
    id,
    grades: grade.map((g) => g.id),
    // demandId: '',
    // grades: [grade?.id],
    // type_of_listing: data[type]?.label,
    rate,
    countryCurrencyId,
    packagingTypeId: packagingTypeId.id,
    validityInHours,
    validityStartTime: Date.parse(validityStartTime),
    validityExpireTime: Date.parse(validityExpireTime),
    totalQty,
    minimumQty,
    minimumLiftingQty,
    city: city?.id,
    state: state?.id,
    status: status?.label,
    trader,
    quantityUnit: quantityUnit.id,
    rateUnit: rateUnit.id,
  };
};

export const getForwardInitialValueForOffer = (data, type) => {
  const {
    id,
    rate,
    rateUnit,
    validityInHours,
    status,
    totalQty,
    minimumQty,
    validityStartTime,
    validityExpireTime,
    minDeliveryDelay,
    maxDeliveryDelay,
    city,
    grade,
    state,
    packagingTypeId,
    trader,
    paymentTerm,
    quantityUnit,
  } = data;
  return {
    id,
    type_of_listing: { id: data[type], label: data[type] },
    grade: { id: grade?.id, label: grade?.name },
    rate,
    status: { id: status, label: status },
    validityInHours,
    packagingTypeId: { id: packagingTypeId?.id, type: packagingTypeId?.type },
    totalQty,
    minimumQty,
    validityStartTime,
    validityExpireTime,
    minDeliveryDelay,
    maxDeliveryDelay,
    state: { id: state?.id, label: state?.stateName },
    city: { id: city?.id, label: city?.cityName },
    trader: trader?.id,
    paymentTerm: paymentTerm?.id,
    rateUnit: { id: rateUnit?.id, label: rateUnit?.isoCurrencyCode },
    quantityUnit: { id: quantityUnit, label: quantityUnit },
  };
};

export const getForwardInitialValueForDemand = (data, type) => {
  const {
    id,
    rate,
    // rateUnit,
    validityInHours,
    status,
    totalQty,
    minimumQty,
    validityStartTime,
    validityExpireTime,
    minDeliveryDelay,
    maxDeliveryDelay,
    city,
    grades,
    state,
    packagingType,
    trader,
    paymentTerm,
    countryCurrency,
    minimumLiftingQty,
    rateUnit,
    quantityUnit,
  } = data;
  return {
    id,
    type_of_listing: { id: data[type], label: data[type] },
    grade: grades,
    rate,
    status: { id: status, label: status },
    validityInHours,
    packagingTypeId: { id: packagingType?.id, type: packagingType?.type },
    totalQty,
    minimumQty,
    validityStartTime,
    validityExpireTime,
    minDeliveryDelay,
    minimumLiftingQty,
    maxDeliveryDelay,
    state: { id: state?.id, label: state?.stateName },
    city: { id: city?.id, label: city?.cityName },
    trader: trader?.id,
    // rateUnitId: rateUnit?.id,
    paymentTerm: paymentTerm?.id,
    countryCurrencyId: countryCurrency?.id,
    rateUnit: { id: rateUnit?.id, label: rateUnit?.isoCurrencyCode },
    quantityUnit: { id: quantityUnit, label: quantityUnit },
  };
};

export const getForwardValuesForOffer = (data) => {
  const {
    id,
    rate,
    paymentTerm,
    // forwardDemandId,
    validityInHours,
    status,
    totalQty,
    minimumQty,
    city,
    grade,
    state,
    packagingTypeId,
    minDeliveryDelay,
    maxDeliveryDelay,
    validityStartTime,
    validityExpireTime,
    rateUnit,
    quantityUnit,
    trader,
  } = data;
  return {
    // type_of_listing: data[type]?.label,

    paymentTerm,
    minDeliveryDelay: Date.parse(moment(minDeliveryDelay).format('YYYY-MM-DD')),
    maxDeliveryDelay: Date.parse(moment(maxDeliveryDelay).format('YYYY-MM-DD')),
    // forwardDemandId,
    id,
    gradeId: grade?.id,
    rate,
    rateUnit: rateUnit.id,
    quantityUnit: quantityUnit.id,
    packagingTypeId: packagingTypeId?.id,
    validityInHours,
    validityStartTime: Date.parse(validityStartTime),
    validityExpireTime: Date.parse(validityExpireTime),
    totalQty,
    minimumQty,
    city: city?.id,
    state: state?.id,
    trader,
    status: status?.label,
  };
};

export const getForwardValuesForDemand = (data) => {
  const {
    id,
    rate,
    paymentTerm,
    validityInHours,
    status,
    totalQty,
    minimumQty,
    minimumLiftingQty,
    city,
    grade,
    state,
    packagingTypeId,
    minDeliveryDelay,
    maxDeliveryDelay,
    countryCurrencyId,
    trader,
    validityExpireTime,
    validityStartTime,
    rateUnit,
    quantityUnit,
    // delivery_date,
  } = data;
  return {
    id,
    minDeliveryDelay: Date.parse(moment(minDeliveryDelay).format('YYYY-MM-DD')),
    maxDeliveryDelay: Date.parse(moment(maxDeliveryDelay).format('YYYY-MM-DD')),
    // type_of_listing: data[type]?.label,
    grades: grade.map((g) => g.id),
    paymentTerm,
    rate,
    countryCurrencyId,
    packagingTypeId: packagingTypeId?.id,
    validityInHours,
    validityStartTime: Date.parse(validityStartTime),
    validityExpireTime: Date.parse(validityExpireTime),
    totalQty,
    minimumQty,
    minimumLiftingQty,
    city: city?.id,
    state: state?.id,
    status: status?.label,
    trader,
    rateUnit: rateUnit.id,
    quantityUnit: quantityUnit.id,
  };
};

// export const getForwardInitialValue = (data, type) => {
//   console.log('____', type, data);

//   const {
//     rate,
//     validityInHours,
//     status,
//     totalQty,
//     minimumQty,
//     city,
//     grade,
//     state,
//     packagingTypeId,
//   } = data;
//   return {
//     type_of_listing: { id: data[type], label: data[type] },
//     grade: { id: grade?.name, label: grade?.name },
//     rate,
//     status: { id: status, label: status },
//     validityInHours,
//     packagingTypeId: packagingTypeId?.type,
//     totalQty,
//     minimumQty,
//     state: { id: state?.id, label: state?.stateName },
//     city: { id: city?.id, label: city?.cityName },
//   };
// };

// export const getForwardInitialValue = (data) => {
//   console.log('____forward', data);

//   // const {
//   //   offerType,
//   //   rate,
//   //   validityInHours,
//   //   status,
//   //   totalQty,
//   //   minimumQty,
//   //   city,
//   //   grade,
//   //   state,
//   //   packagingTypeId,
//   // } = data;
//   return {
//     type_of_listing: { id: data?.offerType, label: data?.offerType },
//     grade: { id: data?.grade?.name, label: data?.grade?.name },
//     status: { id: data.status, label: data.status },
//     packagingTypeId: data?.packagingTypeId?.type,
//     state: data?.state?.stateName,
//     city: data?.city?.cityName,
//     // rate,
//     // validityInHours,
//     // totalQty,
//     // minimumQty,
//     // ...data,
//   };
// };

// export const getForwardInitialValue = (data, type) => {
//   console.log('____', type, data);

//   const {
//     id,
//     rate,
//     rateUnit,
//     validityInHours,
//     status,
//     totalQty,
//     minimumQty,
//     validityStartTime,
//     validityExpireTime,
//     minDeliveryDelay,
//     maxDeliveryDelay,
//     city,
//     grade,
//     state,
//     packagingTypeId,
//     trader,
//     paymentTerm,
//   } = data;
//   return {
//     id,
//     type_of_listing: { id: data[type], label: data[type] },
//     grade: { id: grade?.name, label: grade?.name },
//     rate,
//     status: { id: status, label: status },
//     validityInHours,
//     packagingTypeId: packagingTypeId?.type,
//     totalQty,
//     minimumQty,
//     validityStartTime,
//     validityExpireTime,
//     minDeliveryDelay,
//     maxDeliveryDelay,
//     state: { id: state?.id, label: state?.stateName },
//     city: { id: city?.id, label: city?.cityName },
//     trader: trader?.id,
//     rateUnitId: rateUnit?.id,
//     paymentTerm: paymentTerm?.id,
//   };
// };

// export const getForwardValues = (data, type) => {
//   console.log('____', type, data);

//   const {
//     rate,
//     validityInHours,
//     status,
//     totalQty,
//     minimumQty,
//     city,
//     grade,
//     state,
//     packagingTypeId,
//   } = data;
//   return {
//     type_of_listing: data[type]?.label,
//     grade: grade?.label,
//     rate,
//     status: status?.label,
//     validityInHours,
//     packagingTypeId,
//     totalQty,
//     minimumQty,
//     state: state?.id,
//     city: city?.id,
//   };
// };
// export const getForeignInitialValue = (data, type) => {
//   console.log('____foreign', data);

//   const {
//     rate,
//     validityInHours,
//     status,
//     totalQty,
//     minimumQty,
//     city,
//     grade,
//     state,
//     packagingTypeId,
//     expectedShipmentDate,
//     deliveryTerm,
//     portOfDischarge,
//   } = data;
//   return {
//     type_of_listing: { id: data[type], label: data[type] },
//     grade: { id: grade?.name, label: grade?.name },
//     rate,
//     status: { id: status, label: status },
//     validityInHours,
//     packagingTypeId: packagingTypeId?.type,
//     totalQty,
//     minimumQty,
//     state: { id: state?.id, label: state?.stateName },
//     city: { id: city?.id, label: city?.cityName },
//     expectedShipmentDate,
//     deliveryTerm: { id: deliveryTerm?.term, label: deliveryTerm?.term },
//     portOfDischarge: {
//       id: portOfDischarge?.name,
//       label: portOfDischarge?.name,
//     },
//   };
// };

export const getForeignInitialValueForOffer = (data, type) => {
  const {
    id,
    rate,
    validityInHours,
    status,
    totalQty,
    minimumQty,
    city,
    grade,
    state,
    packagingTypeId,
    expectedShipmentDate,
    deliveryTerm,
    portOfDischarge,
    portOfLoading,
    minShipmentDelay,
    maxShipmentDelay,
    minDeliveryDelay,
    maxDeliveryDelay,
    rateUnit,
    validityStartTime,
    validityExpireTime,
    trader,
    paymentTerm,
    quantityUnit,
  } = data;
  return {
    id,
    type_of_listing: { id: data[type], label: data[type] },
    grade: { id: grade?.id, label: grade?.name },
    rate,
    status: { id: status, label: status },
    validityInHours,
    packagingTypeId: { id: packagingTypeId?.id, type: packagingTypeId?.type },
    totalQty,
    minimumQty,
    state: { id: state?.id, label: state?.stateName },
    city: { id: city?.id, label: city?.cityName },
    expectedShipmentDate,
    deliveryTerm: { id: deliveryTerm?.id, label: deliveryTerm?.term },
    portOfDischarge: {
      id: portOfDischarge?.id,
      label: portOfDischarge?.name,
    },
    portOfLoading: {
      id: portOfLoading?.id,
      label: portOfLoading?.name,
    },
    minShipmentDelay,
    maxShipmentDelay,
    minDeliveryDelay,
    maxDeliveryDelay,
    rateUnit: { id: rateUnit?.id, label: rateUnit?.isoCurrencyCode },
    quantityUnit: { id: quantityUnit, label: quantityUnit },

    validityStartTime,
    validityExpireTime,
    trader: trader?.id,
    paymentTerm: paymentTerm?.id,
  };
};
export const getForeignValuesForOffer = (data) => {
  const {
    id,
    rate,
    validityInHours,
    status,
    totalQty,
    minimumQty,
    city,
    grade,
    state,
    packagingTypeId,
    expectedShipmentDate,
    deliveryTerm,

    rateUnit,
    portOfDischarge,
    portOfLoading,
    paymentTerm,
    minShipmentDelay,
    maxShipmentDelay,
    minDeliveryDelay,
    maxDeliveryDelay,
    validityStartTime,
    validityExpireTime,
    trader,
  } = data;
  return {
    paymentTerm,
    portOfLoading: portOfLoading?.id,
    portOfDischarge: portOfDischarge?.id,
    expectedShipmentDate: Date.parse(expectedShipmentDate),
    minShipmentDelay,
    maxShipmentDelay,
    deliveryTerm: deliveryTerm?.id,

    minDeliveryDelay: Date.parse(moment(minDeliveryDelay).format('YYYY-MM-DD')),
    maxDeliveryDelay: Date.parse(moment(maxDeliveryDelay).format('YYYY-MM-DD')),
    // foreignDemandId: '',
    id,
    // type_of_listing: data[type],
    gradeId: grade?.id,
    rate,
    rateUnit: rateUnit?.id,
    packagingTypeId: packagingTypeId?.id,
    validityInHours,
    validityStartTime: Date.parse(validityStartTime),
    validityExpireTime: Date.parse(validityExpireTime),
    totalQty,
    minimumQty,
    state: state?.id,
    city: city?.id,
    trader,
    status: status.label,
  };
};

export const getForeignInitialValueForDemand = (data, type) => {
  const {
    id,
    rate,
    validityInHours,
    status,
    totalQty,
    minimumQty,
    city,
    grades,
    state,
    packagingType,
    expectedShipmentDate,
    deliveryTerm,
    portOfDischarge,
    countryCurrency,
    validityStartTime,
    validityExpireTime,
    minimumLiftingQty,
    minDeliveryDelay,
    maxDeliveryDelay,
    rateUnit,
    paymentTerm,
    trader,
    quantityUnit,
  } = data;
  return {
    id,
    type_of_listing: { id: data[type], label: data[type] },
    grade: grades,
    rate,
    status: { id: status, label: status },
    validityInHours,
    packagingTypeId: { id: packagingType?.id, type: packagingType?.type },
    totalQty,
    minimumQty,
    validityStartTime,
    validityExpireTime,
    minimumLiftingQty,
    state: { id: state?.id, label: state?.stateName },
    city: { id: city?.id, label: city?.cityName },
    expectedShipmentDate,
    deliveryTerm: { id: deliveryTerm?.id, label: deliveryTerm?.term },
    portOfDischarge: {
      id: portOfDischarge?.id,
      label: portOfDischarge?.name,
    },
    trader: trader?.id,
    countryCurrencyId: countryCurrency?.id,
    paymentTerm: paymentTerm?.id,
    minDeliveryDelay,
    maxDeliveryDelay,
    rateUnit: { id: rateUnit?.id, label: rateUnit?.isoCurrencyCode },
    quantityUnit: { id: quantityUnit, label: quantityUnit },
  };
};

export const getForeignValuesForDemand = (data) => {
  const {
    id,
    rate,
    minDeliveryDelay,
    maxDeliveryDelay,
    validityInHours,
    status,
    totalQty,
    minimumQty,
    city,
    grade,
    state,
    packagingTypeId,
    // expectedShipmentDate,
    // deliveryTerm,
    portOfDischarge,
    paymentTerm,
    countryCurrencyId,
    validityStartTime,
    validityExpireTime,
    minimumLiftingQty,
    trader,
    deliveryTerm,
    rateUnit,
  } = data;
  return {
    // type_of_listing: data[type],
    id,
    portOfDischarge: portOfDischarge?.id,
    paymentTerm,
    minDeliveryDelay: Date.parse(moment(minDeliveryDelay).format('YYYY-MM-DD')),
    maxDeliveryDelay: Date.parse(moment(maxDeliveryDelay).format('YYYY-MM-DD')),
    grades: grade.map((g) => g.id),
    rate,
    countryCurrencyId,
    packagingTypeId: packagingTypeId?.id,
    validityInHours,
    validityStartTime: Date.parse(validityStartTime),
    validityExpireTime: Date.parse(validityExpireTime),
    totalQty,
    minimumQty,
    minimumLiftingQty,
    state: state?.id,
    city: city?.id,
    // expectedShipmentDate,
    // deliveryTerm: deliveryTerm?.label,
    status: status.label,
    deliveryTerm: deliveryTerm?.id,
    rateUnit: rateUnit?.id,
    trader,
  };
};
// export const getForeignValues = (data, type) => {
//   console.log('____foreign', data);

//   const {
//     rate,
//     validityInHours,
//     status,
//     totalQty,
//     minimumQty,
//     city,
//     grade,
//     state,
//     packagingTypeId,
//     expectedShipmentDate,
//     deliveryTerm,
//     portOfDischarge,
//   } = data;
//   return {
//     type_of_listing: data[type],
//     grade: grade?.label,
//     rate,
//     status: status.label,
//     validityInHours,
//     packagingTypeId: packagingTypeId?.type,
//     totalQty,
//     minimumQty,
//     state: state?.id,
//     city: city?.id,
//     expectedShipmentDate,
//     deliveryTerm: deliveryTerm?.label,
//     portOfDischarge: portOfDischarge?.label,
//   };
// };

export const liveValidationSchema = Yup.object({
  // status: Yup.string('Status your name').required('Status is required').trim(),
  rate: Yup.string('Enter the rate').required('Rate is required').trim(),
  validityInHours: Yup.number('Enter the validityInHours')
    .required('ValidityInHours is required')
    .max(24, 'Maximum 24 number')
    .min(1, 'Minimum 1 number'),
  // .trim(),
  minimumQty: Yup.string('Enter the minimumQty')
    .required('MinimumQty is required')
    .trim(),
  totalQty: Yup.string('Enter the totalQty')
    .required('TotalQty is required')
    .trim(),
  // packagingTypeId: Yup.string('Enter the packagingTypeId').required(
  //   'PackagingTypeId is required'
  // ),
  // packagingTypeId: Yup.object().required('Please Select Packaging Type'),
});

export const forwardValidationSchema = Yup.object({
  // status: Yup.string('Status your name').required('Status is required').trim(),
  // rate: Yup.string('Enter the rate').required('Rate is required').trim(),
  // validityInHours: Yup.string('Enter the validityInHours')
  //   .required('ValidityInHours is required')
  //   .trim(),
  // minimumQty: Yup.string('Enter the minimumQty')
  //   .required('MinimumQty is required')
  //   .trim(),
  // totalQty: Yup.string('Enter the totalQty')
  //   .required('TotalQty is required')
  //   .trim(),
  // // packagingTypeId: Yup.string('Enter the packagingTypeId').required(
  // //   'PackagingTypeId is required'
  // // ),
  // packagingTypeId: Yup.object().required('Please select Packaging Type'),

  rate: Yup.string('Enter the rate').required('Rate is required').trim(),
  validityInHours: Yup.number('Enter the validityInHours')
    .required('ValidityInHours is required')
    .max(24, 'Maximum 24 number')
    .min(1, 'Minimum 1 number'),
  // .trim(),
  minimumQty: Yup.string('Enter the minimumQty')
    .required('MinimumQty is required')
    .trim(),
  totalQty: Yup.string('Enter the totalQty')
    .required('TotalQty is required')
    .trim(),
});
export const foreignValidationSchema = Yup.object({
  // status: Yup.string('Status your name').required('Status is required').trim(),
  // rate: Yup.string('Enter the rate').required('Rate is required').trim(),
  // validityInHours: Yup.string('Enter the validityInHours')
  //   .required('ValidityInHours is required')
  //   .trim(),
  // minimumQty: Yup.string('Enter the minimumQty')
  //   .required('MinimumQty is required')
  //   .trim(),
  // totalQty: Yup.string('Enter the totalQty')
  //   .required('TotalQty is required')
  //   .trim(),
  // packagingTypeId: Yup.string('Enter the packagingTypeId').required(
  //   'PackagingTypeId is required'
  // ),

  rate: Yup.string('Enter the rate').required('Rate is required').trim(),
  validityInHours: Yup.number('Enter the validityInHours')
    .required('ValidityInHours is required')
    .max(24, 'Maximum 24 number')
    .min(1, 'Minimum 1 number'),
  // .trim(),
  minimumQty: Yup.string('Enter the minimumQty')
    .required('MinimumQty is required')
    .trim(),
  totalQty: Yup.string('Enter the totalQty')
    .required('TotalQty is required')
    .trim(),

  packagingTypeId: Yup.object().required('Please select Packaging Type'),
});
